import {
  changeProjectFileName,
  getCurrentProjectId,
  getStatus,
  getStoreProjectFilesList,
  selectProjectFile
} from 'modules/projectFles';
import { DEFAULT_PROJECT_NAME } from 'constants/projects';
import { isScenesCreated } from 'modules/sceneDownloadingProgress';
import { FC, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useClickAway, useToggle } from 'react-use';
import { Box, Button, Flex, Text } from 'theme-ui';
import { ProjectFileBase } from 'types/ProjectFiles';
import analytics from 'utils/analytics';
import { FutureNotificationContainer } from './projectFilesBar/FutureNotifications';
import { ProjectFilesMenu } from './projectFilesBar/ProjectFilesMenu';
import { Title } from './projectFilesBar/Title';
import { useTranslator } from '@picsart/rc/services/localization';
import { saveRevision } from 'modules/revisions';
import { ToolName } from 'types/AppState';
import Icon from 'shared/Icon';

const translateProjects = (
  projects: ProjectFileBase[],
  translator: (name: string) => string
): ProjectFileBase[] =>
  projects?.map(item =>
    item.name === DEFAULT_PROJECT_NAME ? { ...item, name: translator(item.name) } : item
  );
const sortProject = (projectList: ProjectFileBase[]) =>
  projectList?.sort((a, b) => b.date - a.date);
type ProjectFilesBarProps = {
  updateProjectFileName: (id: string) => (value: string) => void;
  current: string;
  list: ProjectFileBase[];
  isScenesCreated: boolean;
  setCurrentProject: (id: string, index: number) => void;
};

const ProjectFilesBarBase: FC<ProjectFilesBarProps> = ({
  list,
  current,
  updateProjectFileName,
  setCurrentProject,
  isScenesCreated
}) => {
  const translator = useTranslator();
  const container = useRef<HTMLDivElement>(null);
  const button = useRef(null);
  const [isVisible, toggleVisible] = useToggle(false);
  const [isNotificationVisible, toggleNotificationVisible] = useToggle(
    !JSON.parse(localStorage.getItem('isFirstVisit'))
  );
  const name = list?.find(item => item.id === current)?.name;

  const closeNotification = () => {
    toggleNotificationVisible(!isNotificationVisible);
    localStorage.setItem('isFirstVisit', '1');
  };
  useClickAway(container, ({ target }) => {
    button.current !== target && toggleVisible(false);
  });
  const handleOpenMenu = () => {
    !isVisible && analytics.chooserOpen(list.length);
    toggleVisible(!isVisible);
  };
  return (
    <Box sx={{ position: 'relative', ml: '16px' }}>
      <Flex sx={{ alignItems: 'center' }}>
        <Button
          ref={button}
          onClick={handleOpenMenu}
          variant="primary"
          sx={{
            bg: 'white',
            color: 'black',
            px: '16px',
            '&:hover': {
              bg: '#f8f8f8'
            },
            border: isNotificationVisible ? '2px solid #3a76e8' : '2px solid transparent'
          }}
          data-test="projects-link"
        >
          {translator('My_Projects')}
        </Button>
        <Title name={name} setName={updateProjectFileName(current)} />
        {/* {current && <SaveProcessIndicator />} */}
        {isNotificationVisible && <FutureNotificationContainer closeHandler={closeNotification} />}
      </Flex>
      {isVisible && (
        <ProjectFilesMenu
          isScenesCreated={isScenesCreated}
          current={current}
          list={list}
          containerRef={container}
          setCurrentProject={setCurrentProject}
        />
      )}
    </Box>
  );
};

export const SaveProcessIndicator = () => {
  const status = useSelector(getStatus);
  const current = useSelector(getCurrentProjectId);
  const translator = useTranslator();
  return (
    current && (
      <Flex
        sx={{
          ml: '16px',
          alignItems: 'center',
          position: 'absolute',
          left: '100%',
          top: '50%',
          transform: 'translateY(-50%)'
        }}
      >
        <Icon sx={{ opacity: 0.3 }} name="save-project-editor" />{' '}
        <span sx={{ mx: '4px', color: '#ADADAD' }}>—</span>
        <Text
          variant="body"
          color="white"
          sx={{ fontSize: '14px', fontStyle: 'italic', mb: '-2px', color: '#ADADAD' }}
          data-test="projects-status"
        >
          {status === 'saved' ? translator('Saved') : translator('Saving...')}
        </Text>
      </Flex>
    )
  );
};

export const ProjectFilesBar = () => {
  const dispatch = useDispatch();
  const translator = useTranslator();
  const projectFilesList = translateProjects(
    sortProject(useSelector(getStoreProjectFilesList)),
    translator
  );
  const current = useSelector(getCurrentProjectId);
  const updateProjectNameHandler = (id: string) => (val: string) => {
    if (!id && val === DEFAULT_PROJECT_NAME) return;
    dispatch(changeProjectFileName(id, val));
    dispatch(saveRevision(ToolName.Project));
  };

  const isScenesPrepared = useSelector(isScenesCreated);

  const setCurrentProjectFileHandler = (id: string, index: number) => {
    dispatch(selectProjectFile(id));
    analytics.chooserClick(index);
  };

  return (
    <ProjectFilesBarBase
      list={projectFilesList}
      current={current}
      isScenesCreated={isScenesPrepared}
      updateProjectFileName={updateProjectNameHandler}
      setCurrentProject={setCurrentProjectFileHandler}
    />
  );
};
